/**
 * One answer to "what do we show when the server says no?".
 *
 * Client rule, 2026-10-10: a permission refusal is not a failure. It must read
 * politely and must not be styled as an error.
 *
 * Before this, a 403 surfaced as whatever the raw handler said — most often
 * "Failed to load standard folders." in a red box, which tells the user their
 * software is broken when in fact it is working exactly as configured. A Site
 * Study Designer hitting the binder-template catalog saw precisely that.
 *
 * The companion rule still applies and is the better fix where it is possible:
 * gate the render, not the click — a control a role cannot use should not be
 * drawn at all. This is the safety net for everything that gets through, and
 * for pages whose whole purpose is the thing being refused.
 */
import { ApiError } from '@/api/client';

/** The exact wording the client asked for. Do not reword per-screen. */
export const PERMISSION_DENIED_MESSAGE =
  "You don't have the required permissions to use this feature. "
  + 'Please contact your administrator to request access.';

/**
 * Is this the server declining, rather than something going wrong?
 *
 * 403 only. A 401 is a session problem — the client already redirects to
 * re-auth on those — and a 404 may be a genuinely missing record, which the
 * caller should be free to phrase its own way.
 */
export function isPermissionError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

/**
 * The text to show for a failed read.
 *
 * Replaces the pattern that was repeated ~200 times:
 *   `e instanceof ApiError ? e.message : 'Failed to load X.'`
 *
 * A refusal gets the polite wording; anything else keeps the server's own
 * message, which is usually more useful than the local fallback, and falls
 * back to the caller's text when there is none.
 */
export function errorText(error: unknown, fallback: string): string {
  if (isPermissionError(error)) return PERMISSION_DENIED_MESSAGE;
  if (error instanceof ApiError && error.message) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * Tailwind classes for the container around {@link errorText}.
 *
 * A refusal is informational — neutral surface, muted text, no red. A real
 * failure keeps the danger styling, because that one IS something wrong and
 * should look like it.
 */
export function errorToneClass(error: unknown): string {
  return isPermissionError(error)
    ? 'border border-surface-border bg-surface-subtle text-ink-secondary'
    : 'border border-danger-200 bg-danger-50 text-danger-700';
}
